import { call, put, takeEvery, select, take } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';
import { setUserAccountType } from 'JS/actionCreators/authActions';
import { ACCOUNT_TYPES } from 'JS/config/consts';

import {
  GET_CHECKOUT_INFO_REQUESTED,
  getCheckoutInfoFailure,
  getCheckoutInfoSuccess,
  getCheckoutInfo as getCheckoutInfoAction,
  FS_CHECKOUT_SUCCESS
} from '../actionCreators/checkoutActions';
import { getCheckoutInfo } from '../shared/providers/CheckoutProvider';
import { sendErrorToSentry } from '../logging';
import { VSP_100_MONTHLY } from '../config/licenceType';
import { COUNTRY_REQUEST_SUCCESS, COUNTRY_UPDATE, LOCATION_UPDATE_SUCCESS } from '../actionCreators/locationActions';
import countries from '../config/countries.yaml';
import { GET_EXISTING_PAYMENT_CARD_DETAILS_SUCCESS } from '../actionCreators/paymentActions';

function* launchGetCheckoutInfo() {
  yield put(getCheckoutInfoAction());
}

function* getCheckoutSummary(action) {
  try {
    const { countryCode, currencyCode, state, postcode, isDefaultPricing } = yield select(({ location, payment }) => {
      const { existingPaymentDetails } = payment;
      if (existingPaymentDetails) {
        return {
          countryCode: countries.find(country => existingPaymentDetails.country === country.label)?.isoCode,
          currencyCode: existingPaymentDetails.currency,
          state: existingPaymentDetails.state,
          postcode: existingPaymentDetails.postcode,
          isDefaultPricing: false
        };
      }

      const { country, currency, locality, postalCode, isoCode3 } = location;
      const countryConfig = countries.find(country => country.isoCode3 === isoCode3);
      const requiresLookup = countryConfig?.requiresLookup || false;
      const loadDefaultPricing = requiresLookup && (locality === null || postalCode === null);

      return {
        countryCode: country,
        currencyCode: currency,
        state: loadDefaultPricing ? countryConfig.defaultState : locality,
        postcode: loadDefaultPricing ? countryConfig.defaultPostalCode : postalCode,
        isDefaultPricing: loadDefaultPricing
      };
    });

    if (!countryCode) return;

    const checkoutInfo = yield call(getCheckoutInfo, VSP_100_MONTHLY, countryCode, currencyCode, state, postcode);
    yield put(getCheckoutInfoSuccess(checkoutInfo, VSP_100_MONTHLY, isDefaultPricing));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getCheckoutInfoFailure(e.message));
  }
}

// Waits until the user has dismissed the FastSpring checkout before elevating priviliages
// as to prevent checkout component from being unloaded due to free user protection HOC
function* elevatePrivilegesOnRouteChange() {
  yield take(LOCATION_CHANGE);
  yield put(setUserAccountType(ACCOUNT_TYPES.CREATOR));
}

function* checkoutSagas() {
  yield takeEvery(GET_CHECKOUT_INFO_REQUESTED, getCheckoutSummary);
  yield takeEvery(
    [COUNTRY_UPDATE, LOCATION_UPDATE_SUCCESS, COUNTRY_REQUEST_SUCCESS, GET_EXISTING_PAYMENT_CARD_DETAILS_SUCCESS],
    launchGetCheckoutInfo
  );
  yield takeEvery(FS_CHECKOUT_SUCCESS, elevatePrivilegesOnRouteChange);
}

export default checkoutSagas;
